import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { nothingData } from "@routes/IconeSvg";

import { convertFrom100 } from "@components/evaluation/_helpers/evaluationConverter";

import { Evaluation_Format } from "@helpers/_constants";

import QuestionItem from "./QuestionItem";
import SubjectGroupItem from "./SubjectGroupItem";

const DISPLAY_MAX_BY_FORMAT = Object.fromEntries(
  Object.entries(Evaluation_Format).map(([key, value]) => [
    value.value,
    value.displayedMaxNote || value.maxNote
  ])
);

export default function ObjectiveTable(props) {
  const {
    respondent,
    surveyTypeAndGroup,
    selectedSurveyRequest,
    goToDetail,
    readOnly = false,
    handleShowModalAnswer
  } = props;

  const { t: translate } = useTranslation("assesment.frequency");

  // 🧩 Table Value 🧩
  const selectedSurveyGroups = useMemo(() => {
    return surveyTypeAndGroup.subjectGroups;
  }, [surveyTypeAndGroup]);

  const mappedSurveyGroups = useMemo(() => {
    if (!selectedSurveyGroups.length || !selectedSurveyRequest) {
      return [];
    }

    let output = [];

    for (let surveyGroup of selectedSurveyGroups) {
      let mappedSurveyGroup = {
        ...surveyGroup,
        sum: 0,
        maxSum: 0,
        subjects: []
      };

      for (let surveySubject of surveyGroup.subjects) {
        const surveySubjectNotation =
          selectedSurveyRequest.notations[surveySubject._id];

        if (surveySubjectNotation) {
          mappedSurveyGroup.sum += convertFrom100(
            surveySubjectNotation.note,
            surveySubject.surveyFormat
          );

          mappedSurveyGroup.maxSum +=
            DISPLAY_MAX_BY_FORMAT[surveySubject.surveyFormat];

          mappedSurveyGroup.subjects.push({
            ...surveySubject,
            surveySubjectNotation
          });
        }
      }

      if (mappedSurveyGroup.subjects.length) {
        output.push(mappedSurveyGroup);
      }
    }

    return output;
  }, [selectedSurveyGroups, selectedSurveyRequest]);

  const notationSum = useMemo(() => {
    let sum = 0;
    let maxSum = 0;

    for (let surveyGroup of mappedSurveyGroups) {
      maxSum += surveyGroup.maxSum;
      sum += surveyGroup.sum;
    }

    return `${sum}/${maxSum}`;
  }, [mappedSurveyGroups]);

  return (
    <div className="arh-w-full arh-px-9">
      <div className="arh-mt-4">
        <table className="arh-mt-0 arh-w-full arh-table-fixed arh-items-center">
          <thead className="arh-sticky -arh-top-[1px] arh-z-[2]">
            <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
              <th className="arh-w-[40%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r  arh-border-t-0 arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-start arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Groupe de sondage")}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0  arh-border-r arh-border-t-0 arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Dernière évaluation")}
                <div>
                  <span className="arh-rounded-[55px] arh-bg-[#E6F8EF] arh-px-[10px] arh-text-[11px] arh-text-[#357569]">
                    {notationSum}
                  </span>
                </div>
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Objectifs pour la prochaine évaluation")}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Moyens disponibles")}
              </th>
              <th className="arh-w-[25%] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0  arh-border-solid arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Objectif général")}
              </th>
              <th className="arh-w-[12rem] arh-max-w-md arh-border-b arh-border-l-0 arh-border-r arh-border-t-0 arh-border-solid  arh-border-borderTable arh-bg-greyColor arh-px-4 arh-py-4 arh-text-center arh-align-top arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                {translate("Actions")}
              </th>
            </tr>
          </thead>

          <tbody>
            {mappedSurveyGroups.map((surveyGroup) => (
              <SubjectGroupItem
                key={surveyGroup._id}
                surveyGroup={surveyGroup}
                respondent={respondent}
                readOnly={readOnly}
                goToDetail={
                  goToDetail
                    ? (params) => goToDetail(params, surveyTypeAndGroup._id)
                    : null
                }
              />
            ))}
            {mappedSurveyGroups.length !== 0 && (
              <QuestionItem
                selectedSurveyRequest={selectedSurveyRequest}
                handleShowModalAnswer={handleShowModalAnswer}
              />
            )}
          </tbody>
        </table>
        {mappedSurveyGroups.length === 0 && (
          <div className="arh-full arh-my-14 arh-flex arh-flex-col arh-items-center arh-justify-center arh-font-poppins">
            {nothingData()}
            <p className="arh-m-5 arh-text-base arh-font-medium arh-text-[#949FB0]">
              {translate("Aucune donnée à afficher")}
            </p>
            <p className="arh-w-[32%] arh-text-center arh-text-base arh-font-medium arh-text-[#949FB0]">
              {translate(
                `Veuillez assigner un sondage à cet employé afin d'afficher les sondages correspondantes`
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
